import { useCallback, useMemo, useState } from "react";

import { type SupportZendeskTicketModel } from "@doitintl/cmp-models";

import { platformsData } from "../consts";
import { type PlatformFilter, type PlatformFilterInfo } from "../types";
import { getPlatform } from "../utils";

export type FirestoreFilterCloudResult = {
  selectedPlatformFilter: PlatformFilter[];
  onClickSupportFilter: (filter: PlatformFilter[]) => void;
  filteredTicketsFirestore:
    | Array<SupportZendeskTicketModel & { updatedAtMillis: number; zendeskIdNumeric: number }>
    | undefined;
  supportPlatformsAvailable: PlatformFilterInfo[];
};

/**
 * Hook for filtering support tickets from firestore.
 * @param currentTicketList - The list of support tickets to filter.
 * @returns The filtered tickets and the selected platform filter.
 */
export const useFilterCloudFirestore = (
  currentTicketList?: SupportZendeskTicketModel[]
): FirestoreFilterCloudResult => {
  const [selectedPlatformFilter, setSelectedPlatformFilter] = useState<PlatformFilter[]>([]);

  const supportPlatformsAvailable = useMemo(() => {
    if (!currentTicketList?.length) {
      return [];
    }

    const products = new Set<PlatformFilterInfo>();

    currentTicketList.forEach((zdt) => {
      const productType = getPlatform(zdt);
      if (productType) {
        const product = platformsData.find((item) => item.value === productType);
        if (product) {
          products.add(product);
        }
      }
    });
    return Array.from(products);
  }, [currentTicketList]);

  const filteredTicketsFirestore = useMemo(() => {
    if (!currentTicketList) {
      return undefined;
    }

    const tickets =
      selectedPlatformFilter.length > 0
        ? currentTicketList.filter((zdt) => selectedPlatformFilter.find((product) => product === getPlatform(zdt)))
        : currentTicketList;

    return tickets.map((ticket) => ({
      ...ticket,
      updatedAtMillis: ticket.firestoreUpdatedDate.toMillis(),
      zendeskIdNumeric: parseInt(ticket.zendeskId, 10),
    }));
  }, [currentTicketList, selectedPlatformFilter]);

  const onClickSupportFilter = useCallback((filter: PlatformFilter[]) => {
    setSelectedPlatformFilter(filter);
  }, []);

  return {
    selectedPlatformFilter,
    onClickSupportFilter,
    filteredTicketsFirestore,
    supportPlatformsAvailable,
  };
};
